import React, { useState } from "react";
import { XCircle, ClockFill } from "react-bootstrap-icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { Alert, Col, Row, Spinner, Table, Input, Button } from "reactstrap";
import IconButton from "../../components/Button";
import InvoiceCancel from "../../components/Modals/InvoiceCancel";
import InvoiceUpdateModal from "../../components/Modals/InvoiceUpdate";
import { keys } from "../../query/keys";
import {
  cancelInvoice,
  getClinicInvoices,
  getClinicInvoiceSummary,
  sendReminderEmail,
} from "../../query/queryFunctions";
import {
  amountWithcommas,
  capitalizeFirstLetterOfString,
} from "../../utils/helpers";

const ClinicInvoices = () => {
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInvoiceUpdateModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceId, setInvoiceId] = useState("");
  const [status, setStatus] = useState("overdue");
  const [nextPage, setNextPage] = useState("");
  const [previousPage, setPreviousPage] = useState("");
  const [page, setPage] = useState(1);

  const [invoiceData, setInvoiceData] = useState({});

  const toggleInvoiceUpdateModal = (invoice) => {
    if (invoice) {
      setInvoiceData(invoice);
      setIsInvoiceModalOpen(!isInvoiceUpdateModalOpen);
    } else {
      setIsInvoiceModalOpen(!isInvoiceUpdateModalOpen);
    }
  };

  const toggleModal = (id) => {
    if (id) {
      setInvoiceId(id);
      setIsModalOpen(!isModalOpen);
    } else {
      setIsModalOpen(!isModalOpen);
    }
  };

  const { isLoading, data: invoices } = useQuery(
    [keys.clinicInvoices, status, nextPage, previousPage],
    () => getClinicInvoices({ nextPage, previousPage, status }),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Count and total over every matching invoice, not just this page
  const { data: summary } = useQuery(
    [keys.clinicInvoices, "summary", status],
    () => getClinicInvoiceSummary(status),
    { refetchOnWindowFocus: false }
  );

  const reminderMutation = useMutation(sendReminderEmail, {
    onSuccess: (response) => {
      toast.success(response?.message);
    },
    onError: (error) => {
      toast.error(
        `The reminder could not be sent: ${error?.message || error?.error || "unknown error"}`
      );
    },
  });

  const mutation = useMutation(cancelInvoice, {
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries(keys.clinicInvoices);
      setInvoiceId("");
      setIsModalOpen(!isModalOpen);
    },
  });

  const handleCancelInvoice = () => {
    mutation.mutate(invoiceId);
  };

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
      </div>
    );
  }

  const getInvoicesByFilter = () => invoices?.data || [];

  const handleSendReminderEmail = (invoice) => {
    reminderMutation.mutate(invoice);
  };

  const handleNextPage = () => {
    const page_ = getInvoicesByFilter();
    if (!page_.length) return;
    setPreviousPage("");
    setNextPage(page_[page_.length - 1].id);
    setPage(page + 1);
  };

  const handlePreviousPage = () => {
    const page_ = getInvoicesByFilter();
    if (!page_.length || page === 1) return;
    setNextPage("");
    setPreviousPage(page_[0].id);
    setPage(page - 1);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setNextPage("");
    setPreviousPage("");
    setPage(1);
  };

  // Paging back with ending_before means there is always a later page again
  const canGoNext = Boolean(previousPage) || Boolean(invoices?.has_more);

  return (
    <div className="p-4 mt-5">
      <Row>
        <Col md={12}>
          {/* Always shown: an empty filter result must not hide the filter */}
          {(
            <Row form className="mb-3">
              <Col md={4}>
                <Input
                  type="select"
                  size="lg"
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option selected disabled value="">
                    Choose an option to filter invoice's
                  </option>
                  <option value="all">All</option>
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                  <option value="paid">Paid</option>
                  <option value="void">Void</option>
                  <option value="overdue">Overdue</option>
                </Input>
              </Col>
              <Col md={6}>
                <Alert color={status === "overdue" ? "danger" : "info"}>
                  All {status !== "all" && status} Invoices (
                  {summary ? summary.count : "…"})-
                  <b>
                    ${summary ? amountWithcommas(summary.total / 100) : "…"}
                  </b>
                </Alert>
              </Col>
            </Row>
          )}

          <Table responsive bordered hover>
            <thead>
              <tr className="text-center">
                <th>Invoice #</th>
                <th>Transaction ID</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Customer</th>
                <th>Created Date</th>
                <th>Due Date</th>
                <th>Note</th>
                <th>Paid Status</th>
                <th colSpan={3}>Action</th>
              </tr>
            </thead>

            <tbody>
              {getInvoicesByFilter()?.length > 0 ? (
                getInvoicesByFilter()?.map((invoice, index) => (
                  <tr key={index}>
                    <td>{invoice?.number}</td>
                    <td className="text-center">
                      {invoice?.payment_intent?.slice(21)}
                    </td>
                    <td className="text-center">${invoice?.total / 100}</td>
                    <td className="text-center">
                      {capitalizeFirstLetterOfString(invoice?.status)}
                    </td>
                    <td>{invoice?.customer_email}</td>
                    <td>
                      {new Date(invoice?.created * 1000).toLocaleDateString()}
                    </td>
                    <td>
                      {new Date(invoice?.due_date * 1000).toLocaleDateString()}
                    </td>
                    <td>{invoice?.description}</td>
                    <td>
                      {invoice?.status === "void" ? (
                        <span className="badge badge-danger">Void</span>
                      ) : new Date(invoice?.due_date * 1000) < Date.now() &&
                        invoice?.status !== "paid" ? (
                        <span className="badge badge-danger">Overdue</span>
                      ) : new Date(invoice?.due_date * 1000) > Date.now() &&
                        invoice?.status !== "paid" ? (
                        <span className="badge badge-warning">Pending</span>
                      ) : (
                        <span className="badge badge-success">Paid</span>
                      )}
                    </td>

                    <td className="text-center">
                      <IconButton
                        title="Reminder"
                        color="primary"
                        size="sm"
                        disabled={
                          ["draft", "void", "paid"].includes(invoice.status) ||
                          reminderMutation.isLoading
                        }
                        icon={<ClockFill className="mr-2" />}
                        onClick={() => handleSendReminderEmail(invoice)}
                      />
                    </td>
                    <td>
                      <IconButton
                        title="Cancel"
                        color="danger"
                        size="sm"
                        disabled={["draft", "paid", "void"].includes(
                          invoice?.status
                        )}
                        icon={<XCircle className="mr-2" />}
                        onClick={() => toggleModal(invoice?.id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10}>
                    <Alert color="info">
                      No Invoice history found for that customer
                    </Alert>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "5px",
              marginBottom: "5px",
            }}
          >
            <Button
              color="primary"
              onClick={handlePreviousPage}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              color="primary"
              onClick={handleNextPage}
              disabled={!canGoNext}
            >
              Next
            </Button>
          </div>
        </Col>

        <InvoiceCancel
          isOpen={isModalOpen}
          toggle={toggleModal}
          handleSubmit={handleCancelInvoice}
          isLoading={mutation.isLoading}
          isError={mutation?.isError}
          error={mutation?.error?.message}
        />

        <InvoiceUpdateModal
          isOpen={isInvoiceUpdateModalOpen}
          toggle={toggleInvoiceUpdateModal}
          invoice={invoiceData}
          handleSubmit={handleCancelInvoice}
          isLoading={mutation.isLoading}
          isError={mutation?.isError}
          error={mutation?.error?.message}
        />
      </Row>
    </div>
  );
};

export default ClinicInvoices;
